/*
This file is located in the main branch
Last time the file was updated (UTC): [02.07.2024 18:53:04]
Permissions to update outside the main branch: [false]
*/

import {
    DropDown,
    useExpandableContext,
    ValidatedDropDownList,
    ValidatedInput,
} from '@finbeat/react-toolkit';
import { faTrashCan } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BOProps } from 'base-components/BOProps/BOProps';
import useAuth from 'hooks/useAuth';
import { CreditProduct } from 'models/api/creditProduct';
import { TariffGroup, TariffGroupCreditProduct } from 'models/api/tariffGroup';
import { useCreditProductsList } from 'providers/CreditProductsProvider';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardBody, CardHeader, Col, Collapse, Row, Spinner } from 'reactstrap';

export type TariffGroupCreditProductFormWrapperProps = {
    id: number | undefined;
    name: string | undefined;
    children: React.ReactNode;
    onDelete: () => void;
    disabled: boolean;
};

const TariffGroupCreditProductFormWrapper = ({
    id,
    name,
    children,
    onDelete,
    disabled,
}: TariffGroupCreditProductFormWrapperProps) => {
    const { isExpanded, expandToggle } = useExpandableContext();
    const [deleteButtonVisible, setDeleteButtonVisible] = useState(false);

    return (
        <Card>
            <CardHeader
                onClick={() => id && expandToggle(id)}
                onMouseOver={() => {
                    setDeleteButtonVisible(true);
                }}
                onMouseLeave={(e) => {
                    setDeleteButtonVisible(false);
                }}
            >
                {name}
                {!disabled && deleteButtonVisible && (
                    <FontAwesomeIcon
                        role={'button'}
                        icon={faTrashCan}
                        onClick={(e) => {
                            e.stopPropagation();
                            onDelete();
                        }}
                        className="float-end text-danger mt-1"
                    />
                )}
            </CardHeader>
            <Collapse isOpen={id === undefined || isExpanded(id)}>
                <CardBody>
                    <div className="accordion">{children}</div>
                </CardBody>
            </Collapse>
        </Card>
    );
};

export type TariffGroupCreditProductValidatedFormProps = TariffGroupCreditProduct & {
    idx: number;
    disabled: boolean;
    showBaseCheckbox: boolean;
};

const TariffGroupCreditProductValidatedForm = ({
    idx,
    disabled,
    showBaseCheckbox,
}: TariffGroupCreditProductValidatedFormProps) => {
    const { creditProducts, isCreditProductsFetching } = useCreditProductsList();

    const { t } = useTranslation();
    const { hasRole } = useAuth();

    const creditProductsList = [{} as CreditProduct, ...creditProducts];

    const creditProductsOptions = creditProductsList.map<DropDown>(
        ({ id, name }: CreditProduct) => ({ id, name })
    );

    if (isCreditProductsFetching) {
        return <Spinner />;
    }

    return (
        <>
            {hasRole('baseTariffGropCheckBox') && showBaseCheckbox && (
                <>
                    <Row>
                        <Col>
                            <ValidatedInput<TariffGroup>
                                type="checkbox"
                                id={'tariffGroupCreditProducts-isBase-' + idx}
                                name={`tariffGroupCreditProducts.${idx}.isBase`}
                                label={t('BackOfficeBase')}
                                disabled={disabled}
                            />
                        </Col>
                    </Row>
                    <hr />
                </>
            )}
            <Row>
                <Col>
                    <ValidatedDropDownList<TariffGroup>
                        name={`tariffGroupCreditProducts.${idx}.creditProduct.id`}
                        label={t('BackOfficeTariff')}
                        items={creditProductsOptions}
                        disabled={disabled}
                    />
                </Col>
            </Row>
            <Row>
                <Col>
                    <ValidatedInput<TariffGroup>
                        type="text"
                        name={`tariffGroupCreditProducts.${idx}.minSum`}
                        label={t('BackOfficeMinAmount')}
                        min={0}
                        disabled={disabled}
                    />
                </Col>
                <Col>
                    <ValidatedInput<TariffGroup>
                        type="text"
                        name={`tariffGroupCreditProducts.${idx}.maxSum`}
                        label={t('BackOfficeMaxAmount')}
                        min={0}
                        disabled={disabled}
                    />
                </Col>
            </Row>
            <hr />
            <Row>
                <Col>
                    <BOProps<TariffGroup> name={`tariffGroupCreditProducts.${idx}.props`} />
                </Col>
            </Row>
        </>
    );
};

export { TariffGroupCreditProductFormWrapper, TariffGroupCreditProductValidatedForm };
